import posthog from "posthog-js";

import { isPostHogEnabled } from "./posthogConfig";

export type AnalyticsProperties = Record<
  string,
  boolean | number | string | null | undefined
>;

const SENSITIVE_KEY_PATTERNS = [
  "email",
  "name",
  "phone",
  "address",
  "dob",
  "birth",
  "ssn",
  "tax",
  "passport",
  "iban",
  "swift",
  "bic",
  "aba",
  "account",
  "routing",
  "wire",
  "amount",
  "balance",
  "nav",
  "commitment",
  "investment",
  "answer",
  "questionnaire",
  "kyc",
  "compliance",
  "password",
  "token",
  "secret",
];

const isSensitiveKey = (key: string): boolean => {
  const normalised = key.toLowerCase();

  return SENSITIVE_KEY_PATTERNS.some((pattern) => normalised.includes(pattern));
};

const guardProperties = (
  properties?: AnalyticsProperties,
): AnalyticsProperties | undefined => {
  if (!properties) return properties;

  if (import.meta.env.IS_VITEST) return properties;

  const flagged = Object.keys(properties).filter(isSensitiveKey);

  if (flagged.length === 0) return properties;
  console.warn(
    `[analytics] Dropped potentially sensitive propert${
      flagged.length === 1 ? "y" : "ies"
    } before sending to PostHog: ${flagged.join(", ")}. ` +
      "Never send PII, financial, banking, questionnaire, or KYC data.",
  );

  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !isSensitiveKey(key)),
  );
};

const runWhenEnabled = (action: () => void): void => {
  if (!isPostHogEnabled()) return;

  try {
    action();
  } catch {
    // Swallow — analytics failures must never break the app
  }
};

export const analytics = {
  capture(eventName: string, properties?: AnalyticsProperties): void {
    runWhenEnabled(() => {
      posthog.capture(eventName, guardProperties(properties));
    });
  },

  identify(userId: string, properties?: AnalyticsProperties): void {
    runWhenEnabled(() => {
      posthog.identify(userId, guardProperties(properties));
    });
  },

  register(properties: AnalyticsProperties): void {
    runWhenEnabled(() => {
      const guarded = guardProperties(properties);

      if (guarded) posthog.register(guarded);
    });
  },

  reset(): void {
    runWhenEnabled(() => {
      posthog.reset();
    });
  },

  group(
    groupType: string,
    groupKey: string,
    groupProperties?: AnalyticsProperties,
  ): void {
    runWhenEnabled(() => {
      posthog.group(groupType, groupKey, guardProperties(groupProperties));
    });
  },
};
